import { useEffect } from "react";

import { noop } from "neetocist";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

import EmbedRenderer from "./EmbedRenderer";
import EmbedSelector from "./EmbedSelector";
import "./product-embed.scss";
import useProductEmbedStore from "./useProductEmbedStore";
import { toCamelCasedString } from "./utils";

const ProductEmbed = ({
  title,
  titleHelpPopoverProps = {},
  subTitleHelpPopoverProps = {},
  className = "",
  customEmbedScriptPath = "",
  customPreviewIframeUrl = "",
  goBackLink = "",
  id,
  isQueryParamsEnabled = false,
  queryParamsProps = {},
  options = {},
  extraArgs = {},
  otherCustomizations = noop,
  inlineWrapperStyle = "height: 90vh; width: 100%;",
  iframeStyle = "height: 100%; width: 100%;",
  supportedLanguages = ["html"],
}) => {
  const { search } = useLocation();

  const queryParams = new URLSearchParams(search);
  const selectedEmbed = toCamelCasedString(queryParams.get("type"));

  const { setProductEmbedState } = useProductEmbedStore.pick();

  useEffect(() => {
    setProductEmbedState({ isQueryParamsEnabled, queryParamsProps });
  }, []);

  return selectedEmbed ? (
    <EmbedRenderer
      {...{
        className,
        customEmbedScriptPath,
        customPreviewIframeUrl,
        extraArgs,
        id,
        iframeStyle,
        inlineWrapperStyle,
        options,
        otherCustomizations,
        queryParamsProps,
        selectedEmbed,
        supportedLanguages,
      }}
    />
  ) : (
    <EmbedSelector
      {...{
        goBackLink,
        options,
        subTitleHelpPopoverProps,
        title,
        titleHelpPopoverProps,
      }}
    />
  );
};

const customizationSwitchShape = PropTypes.shape({
  value: PropTypes.bool,
  label: PropTypes.string,
  helpIconProps: PropTypes.object,
});

ProductEmbed.propTypes = {
  /**
   * To set the title in the embed selection page.
   */
  title: PropTypes.string,
  /**
   * To set props for the HelpPopover component in the title.
   */
  titleHelpPopoverProps: PropTypes.object,
  /**
   * To set props for the HelpPopover component in the sub title.
   */
  subTitleHelpPopoverProps: PropTypes.object,
  /**
   * To set custom classNames. (optional)
   */
  className: PropTypes.string,
  /**
   * To use a different embed script. Uses /javascript/embed.js by default. (optional)
   */
  customEmbedScriptPath: PropTypes.string,
  /**
   * To use a different iframeUrl in Product embed preview. Uses /embed/${id} by default. (optional)
   */
  customPreviewIframeUrl: PropTypes.string,
  /**
   * To  specify extra arguments to pass to the embed script. (optional)
   */
  extraArgs: PropTypes.object,
  /**
   * To show the 'Back to share' button. (optional)
   */
  goBackLink: PropTypes.string,
  /**
   * To specify the entity ID.
   */
  id: PropTypes.string,
  /**
   * To specify the Embed options for the product.
   */
  options: PropTypes.shape({
    inline: PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
      embedIcon: PropTypes.func,
      width: PropTypes.number,
      height: PropTypes.number,
      elementSelector: PropTypes.string,
      dynamicHeight: customizationSwitchShape,
      hideTitle: customizationSwitchShape,
      alignContentLeft: customizationSwitchShape,
      transparentBackground: customizationSwitchShape,
    }),
    floatingPopup: PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
      embedIcon: PropTypes.func,
      btnTextColor: PropTypes.string,
      btnText: PropTypes.string,
      btnPosition: PropTypes.string,
      btnColor: PropTypes.string,
      icon: PropTypes.func,
      showIcon: PropTypes.bool,
    }),
    elementPopup: PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
      embedIcon: PropTypes.func,
      elementSelector: PropTypes.string,
    }),
    iframe: PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
      embedIcon: PropTypes.func,
      width: PropTypes.number,
      height: PropTypes.number,
      iframeUrl: PropTypes.string,
      dynamicHeight: customizationSwitchShape,
      hideTitle: customizationSwitchShape,
      alignContentLeft: customizationSwitchShape,
      transparentBackground: customizationSwitchShape,
    }),
  }),
  /**
   * To enable query parameters
   */
  isQueryParamsEnabled: PropTypes.bool,
  /**
   * To specify the query parameters
   */
  queryParamsProps: PropTypes.shape({
    helpPopoverProps: PropTypes.shape({
      title: PropTypes.string,
      size: PropTypes.oneOf(["default", "large"]),
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      helpLinkProps: PropTypes.object,
      popoverProps: PropTypes.object,
      className: PropTypes.string,
      iconColor: PropTypes.string,
      icon: PropTypes.node,
    }),
    label: PropTypes.string,
    keyPlaceholder: PropTypes.string,
    valuePlaceholder: PropTypes.string,
    addButtonLabel: PropTypes.string,
  }),
  /**
   * To add other customization fields to all the embed options
   */
  otherCustomizations: PropTypes.func,
  /**
   * To override the styles of inline embed wrapper div
   */
  inlineWrapperStyle: PropTypes.string,
  /**
   * To override the styles of inline embed iframe
   */
  iframeStyle: PropTypes.string,
  /**
   * To specifies the supported languages, currently limited to `html` and `react`.
   */
  supportedLanguages: PropTypes.arrayOf(PropTypes.oneOf(["html", "react"])),
};

export default ProductEmbed;
